import type { EmploymentType } from './types';

/**
 * t567u: the one place contract (employment) types get their display labels, in Title Case ("Full Time"), used
 * wherever a contract type is shown (My work, people lists and filters, the employment editor, bulk edit, imports,
 * phone web and iOS). Display only: stored values stay full_time, part_time, zero_hours and casual.
 */
export const CONTRACT_TYPE_LABEL: Record<EmploymentType, string> = { full_time: 'Full Time', part_time: 'Part Time', zero_hours: 'Zero Hours', casual: 'Casual' };
/** Labels for contract types other systems use, so an unexpected value still reads well. */
const OTHER_CONTRACT_TYPE_LABEL: Record<string, string> = {
  fixed_term: 'Fixed Term', temporary: 'Temporary', temp: 'Temporary', permanent: 'Permanent', seasonal: 'Seasonal', apprentice: 'Apprentice',
  apprenticeship: 'Apprenticeship', agency: 'Agency', contractor: 'Contractor', freelance: 'Freelance', bank: 'Bank', annualised_hours: 'Annualised Hours',
};
/** "full_time" / "full time" / "FULL-TIME" → "Full Time"; anything unknown is Title Cased; empty → ''. */
export function contractTypeLabel(v: string | null | undefined): string {
  const key = (v ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!key) return '';
  const known = (CONTRACT_TYPE_LABEL as Record<string, string>)[key] ?? OTHER_CONTRACT_TYPE_LABEL[key];
  if (known) return known;
  return key.split('_').filter(Boolean).map(w => w[0]!.toUpperCase() + w.slice(1)).join(' ');
}
/** The contract types to pick from, in order, with their labels. */
export const CONTRACT_TYPE_OPTIONS: { id: EmploymentType; label: string }[] = (Object.keys(CONTRACT_TYPE_LABEL) as EmploymentType[]).map(id => ({ id, label: CONTRACT_TYPE_LABEL[id] }));

/**
 * Feedback 1 #3: contracts are "N hours a week"; days are optional. A contract can fix the days (5 days),
 * bound them (3-4 days) or leave them open ("any days"). Nothing here assumes a day count unless one is set.
 */
export interface ContractTerms {
  contracted_hours_per_week: number | null;
  contracted_days_per_week?: number | null;
  contracted_min_days_per_week?: number | null;
  contracted_max_days_per_week?: number | null;
}

export interface ContractIssue { code: 'over_hours' | 'over_days' | 'under_days'; severity: 'warning' | 'info'; message: string }

const fmtH = (h: number) => `${Math.round(h * 100) / 100}h`;

/** Day bounds implied by the contract: a fixed pattern is both min and max. */
export function contractDayBounds(c: ContractTerms): { min: number | null; max: number | null } {
  if (c.contracted_days_per_week) return { min: c.contracted_days_per_week, max: c.contracted_days_per_week };
  return { min: c.contracted_min_days_per_week ?? null, max: c.contracted_max_days_per_week ?? null };
}

/** "30h/wk · any days", "37.5h/wk · 5 days", "30h/wk · 3–4 days", "30h/wk · up to 4 days". */
export function contractLabel(c: ContractTerms): string | null {
  if (c.contracted_hours_per_week == null) return null;
  const { min, max } = contractDayBounds(c);
  const days = min != null && max != null ? (min === max ? `${min} day${min === 1 ? '' : 's'}` : `${min}–${max} days`)
    : max != null ? `up to ${max} days` : min != null ? `at least ${min} days` : 'any days';
  return `${fmtH(c.contracted_hours_per_week)}/wk · ${days}`;
}

/**
 * Week check used by the schedule's live totals, drag warnings and the scheduled-hours report.
 * Hours are compared with the weekly hours; days only when the contract states them.
 * `partialWeek` (e.g. a report range shorter than a week) suppresses "under" findings.
 */
export function contractWeekIssues(c: ContractTerms, week: { minutes: number; days: number }, name = 'They', partialWeek = false): ContractIssue[] {
  const out: ContractIssue[] = [];
  const h = c.contracted_hours_per_week;
  if (h != null && week.minutes / 60 > h + 1e-9) out.push({ code: 'over_hours', severity: 'warning', message: `${name} would be on ${fmtH(week.minutes / 60)} - over ${fmtH(h)} contracted` });
  const { min, max } = contractDayBounds(c);
  if (max != null && week.days > max) out.push({ code: 'over_days', severity: 'warning', message: `${name} would work ${week.days} days - contract allows ${max}` });
  if (!partialWeek && min != null && week.days < min && week.days > 0) out.push({ code: 'under_days', severity: 'info', message: `${name} has ${week.days} day${week.days === 1 ? '' : 's'} - contract expects at least ${min}` });
  return out;
}

/** Validation for the employee editor and API. Returns an error message or null. */
export function validateContract(c: ContractTerms): string | null {
  const { contracted_days_per_week: fixed, contracted_min_days_per_week: min, contracted_max_days_per_week: max } = c;
  if (fixed && (min != null || max != null)) return 'Use either fixed days or a min/max range, not both';
  if (min != null && max != null && min > max) return 'Minimum days can’t be more than maximum days';
  if (c.contracted_hours_per_week != null && max != null && c.contracted_hours_per_week > max * 24) return 'More hours than fit in the maximum days';
  return null;
}
